'use client';

import { ImageGalleryItem } from '@type/media-gallery';
import {
  Card, ImageBj, MediaGallery,
} from '@components/common';
import { cn } from '@utils/cn';
import { commonStyles } from '@components/templates/employer/profile/common-styles';

// Interface
// ************************************
interface EmployerGalleryProps {
  images: string[];
  employerName?: string;
}

/**
 * @description Employer Gallery component. Renders an image gallery for the employer.
 *
 * - **employerName** - string. The name of the employer (used for alt text in images).
 * - **images** - string[]. An array of image URLs to display in the gallery.
 */
export const EmployerGallery = (props: EmployerGalleryProps) => {
  // Destructure props
  const { images, employerName } = props;

  // Do not render the component if there are no images
  if (!images.length) {
    return null;
  }

  // Render component
  // *****************************************
  return (
    <Card className={cn(commonStyles.card, 'my-6')}>
      <MediaGallery
        items={images.map(
          (image, index): ImageGalleryItem => ({
            type: 'image',
            src: image,
            thumbnail: image,
            alt: `${employerName}${index + 1}`,
          }),
        )}
      >
        {(openGalleryAtIndex) => (
          <div className="scrollbar -mb-2 grid auto-cols-[calc(50%_-_2rem)] grid-flow-col gap-x-4 overflow-auto pb-2 md:auto-cols-[calc(calc(100%_/_3)_-_3rem)]">
            {images.map((image, index) => (
              <a
                key={image}
                href={image}
                className="w-full rounded border border-input p-1"
                onClick={(e) => {
                  e.preventDefault();
                  openGalleryAtIndex(index);
                }}
              >
                {image && (
                  <div className="relative aspect-[4/3]">
                    <ImageBj
                      src={image}
                      imgFolder="employer_gallery"
                      extractImageName
                      alt={`${employerName}${index + 1}`}
                      className="absolute inset-0 rounded object-cover"
                      aspectRatio="4/3"
                      width="300"
                      height="225"
                    />
                  </div>
                )}
              </a>
            ))}
          </div>
        )}
      </MediaGallery>
    </Card>
  );
};
